import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { CartContext, type CartContextType, type CartItem } from './useCartContext';

const STORAGE_KEY = 'sirena_cart_v1';

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const savedCart = raw ? (JSON.parse(raw) as CartItem[]) : [];

    // A tour can only have one booking configuration per date. Older sessions
    // may contain duplicated rows, so keep the most recent configuration.
    return savedCart.reduce<CartItem[]>((items, item) => {
      const existingIndex = items.findIndex(
        (existing) => existing.tourId === item.tourId && existing.date === item.date,
      );
      if (existingIndex === -1) return [...items, item];
      const next = [...items];
      next[existingIndex] = { ...item, id: next[existingIndex].id };
      return next;
    }, []);
  } catch {
    return [];
  }
}

function saveCart(cart: CartItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartItem[]>(loadCart);

  useEffect(() => {
    saveCart(cart);
  }, [cart]);

  const addItem = useCallback((item: Omit<CartItem, 'id'>) => {
    setCart((prev) => {
      const existingIndex = prev.findIndex(
        (existing) => existing.tourId === item.tourId && existing.date === item.date,
      );
      if (existingIndex === -1) {
        return [...prev, { ...item, id: `${item.tourId}_${item.date}_${Date.now()}` }];
      }

      const next = [...prev];
      next[existingIndex] = { ...item, id: next[existingIndex].id };
      return next;
    });
  }, []);

  const removeItem = useCallback((id: string) => {
    setCart((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const updateItem = useCallback((id: string, updates: Partial<Omit<CartItem, 'id'>>) => {
    setCart((prev) =>
      prev.map((i) => (i.id === id ? { ...i, ...updates } : i))
    );
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const totalItems = cart.length;

  const totalPrice = cart.reduce((sum, item) => {
    return (
      sum +
      item.adults * item.adultPrice +
      item.children * item.childPrice +
      item.babies * item.babyPrice
    );
  }, 0);

  const value: CartContextType = {
    cart,
    addItem,
    removeItem,
    updateItem,
    clearCart,
    totalItems,
    totalPrice,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}
